import { AttachInternals, Host, Component, Prop, State, Event, EventEmitter, h, Element } from '@stencil/core';

@Component({
  tag: 'spw-select',
  styleUrl: 'spw-select.scss',
  formAssociated: true,
  shadow: true,
})
export class SpwSelect {
  @Element() el: HTMLSpwSelectElement;

  /** Emet un event si la valeur change */
  @Event() valueChanged: EventEmitter<string | number>;
  @State() internalValue: string | number;

  /** Valeur sélectionnée actuelle, mutable et reflétée dans le DOM */
  @Prop({ mutable: true, reflect: true }) value: string | number;
  /** Label associé au champ de sélection */
  @Prop() label: string = '';
  /** Texte du placeholder à afficher lorsque rien n'est sélectionné */
  @Prop() placeholder: string = 'Sélectionner une option';
  /** Indique si le champ de sélection est désactivé */
  @Prop() disabled: boolean;
  /** Nom de l'élément utilisé dans les formulaires pour associer la valeur sélectionnée */
  @Prop() name: string;
  /** Indique si la sélection est obligatoire dans le formulaire */
  @Prop() required: boolean = false;
  /** Taille du champ de sélection : 'large' ou 'medium' */
  @Prop() size: 'large' | 'medium' = 'medium';
  /** Texte d'assistance affiché sous le champ de sélection */
  @Prop() assistiveText?: string;
  /** Message d'erreur à afficher sous le champ de sélection */
  @Prop() error?: string = '';
  /** Message d'indication (hint) à afficher sous le champ de sélection */
  @Prop() hint?: string = '';
  /** Message de succès à afficher sous le champ de sélection */
  @Prop() success?: string = '';
  /** Affiche une icône d'erreur si vrai */
  @Prop() showErrorIcon?: boolean = true;
  /** Affiche une icône d'indication si vrai */
  @Prop() showHintIcon?: boolean = true;
  /** Affiche une icône de succès si vrai */
  @Prop() showSuccessIcon?: boolean = true;

  private selectElement!: HTMLSelectElement;

  @AttachInternals() internals: ElementInternals;

  componentWillLoad() {
    this.internalValue = this.value || this.getInitialSelectedValue();
  }

  componentDidLoad() {
    this.internals.setFormValue(this.getFormValue());
    this.moveOptionsToLightDom();
  }

  private getFormValue(): string | null {
    return this.internalValue ? String(this.internalValue) : null;
  }

  private getInitialSelectedValue(): string | number | null {
    const assignedNodes = Array.from(this.el.querySelectorAll('option')) as HTMLOptionElement[];
    for (const option of assignedNodes) {
      if (option.hasAttribute('selected')) {
        return option.getAttribute('value');
      }
    }
    return null;
  }

  private handleSelectChange(event: Event) {
    const select = event.target as HTMLSelectElement;
    this.internalValue = select.value;
    this.value = this.internalValue;
    this.internals.setFormValue(this.getFormValue());
    this.valueChanged.emit(this.internalValue);
  }

  private moveOptionsToLightDom() {
    const slot = this.el.shadowRoot.querySelector('slot');
    if (slot) {
      const assignedNodes = slot.assignedNodes({ flatten: true }) as HTMLElement[];
      assignedNodes.forEach(node => {
        if (node.nodeName === 'OPTION' || node.nodeName === 'OPTGROUP') {
          this.selectElement.appendChild(node);
        }
      });
    }
  }

  private renderErrorMessage() {
    if (this.error) {
      return (
        <spw-field-message variant="error" showIcon={this.showErrorIcon}>
          {this.error}
        </spw-field-message>
      );
    }
  }

  private renderHintMessage() {
    if (this.hint) {
      return (
        <spw-field-message variant="hint" showIcon={this.showHintIcon}>
          {this.hint}
        </spw-field-message>
      );
    }
  }

  private renderSuccessMessage() {
    if (this.success) {
      return (
        <spw-field-message variant="success" showIcon={this.showSuccessIcon}>
          {this.success}
        </spw-field-message>
      );
    }
  }

  private get elementClass(): { [className: string]: boolean } {
    return {
      'spw-select': true,
      [`spw-select--${this.size}`]: true,
      'spw-select--disabled': this.disabled,
      'spw-select--is-error': !!this.error,
    };
  }

  render() {
    return (
      <Host>
        <div class={this.elementClass}>
          {this.label && <spw-field-label label={this.label} name={this.name} required={this.required}></spw-field-label>}
          {this.assistiveText && (
            <spw-field-message class="spw-tw-mb-2 -spw-tw-mt-1" variant="hint" showIcon={false}>
              {this.assistiveText}
            </spw-field-message>
          )}
          <div class="spw-select__container">
            <select
              id={this.name}
              class="spw-select__trigger"
              onChange={event => this.handleSelectChange(event)}
              disabled={this.disabled}
              ref={el => (this.selectElement = el as HTMLSelectElement)}
              tabindex={this.disabled ? -1 : 0}
            >
              <option value="" disabled selected={!this.internalValue}>
                {this.placeholder}
              </option>
            </select>
            <spw-icon class="spw-select__arrow" icon="fa-chevron-down"></spw-icon>
          </div>
          <slot />
        </div>
        {this.renderErrorMessage()}
        {this.renderHintMessage()}
        {this.renderSuccessMessage()}
      </Host>
    );
  }
}
